'use client'
import React, { useState } from 'react'
import { useFormik } from 'formik'
import { resetPasswordValidationSchema } from '@/src/validations/formvalidations'
import { Button } from '@heroui/react'
import { toast } from '@/src/lib/toast'
import { EyeClosedIcon, EyeOpenIcon } from '@/src/ui/AllSVG'
import type { AuthView } from './LoginSignupIndex'
import { useUserResetPasswordMutation } from '@/src/store/rtkQueries/userAuthApi'
import { clearAuthCookies } from '@/src/utils/authCookies'

interface ResetPasswordProps {
    navigate: (view: AuthView) => void
    token?: string
}

export default function ResetPassword({ navigate, token }: ResetPasswordProps) {
    const [showNew, setShowNew] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [userResetPassword, { isLoading: isSubmitting }] = useUserResetPasswordMutation()

    const { values, errors, touched, handleChange, handleBlur, handleSubmit } = useFormik({
        initialValues: {
            newPassword: '',
            confirmPassword: '',
        },
        validationSchema: resetPasswordValidationSchema,
        onSubmit: async (formValues) => {
            try {
                const response = await userResetPassword({
                    token,
                    payload: {
                        password: formValues.newPassword,
                        confirm_password: formValues.confirmPassword,
                    },
                })
                if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                    toast.success(response?.data?.message || 'Password updated')
                    clearAuthCookies()
                    navigate('SignIn')
                }
            } catch (error) {
                console.error('change password', error)
            }
        },
    })

    return (
        <div className="flex flex-col gap-6 w-full">
            {/* Heading */}
            <div className="space-y-2 text-body-color">
                <h1 className="secondary_heading">
                    Forgot <span className="font-secondary italic">Password?</span>
                </h1>
                <p className="text-sm">
                    By creating an account, I am also consenting to receive SMS, messages and emails.
                </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* New Password */}
                <div className="input_container">
                    <label className="form_label">New Password</label>
                    <div className="input_icon_container">
                        <input
                            type={showNew ? 'text' : 'password'}
                            name="newPassword"
                            value={values.newPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="form_input"
                        />
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowNew(p => !p)}
                            className="shrink-0 px-3 text-body-color/40 hover:text-body-color/70 transition-colors"
                            aria-label={showNew ? 'Hide password' : 'Show password'}
                        >
                            {showNew ? <EyeOpenIcon /> : <EyeClosedIcon />}
                        </button>
                    </div>
                    {errors.newPassword && touched.newPassword && (
                        <p className="error_text">{errors.newPassword}</p>
                    )}
                </div>

                {/* Confirm Password */}
                <div className="input_container">
                    <label className="form_label">Confirm Password</label>
                    <div className="input_icon_container">
                        <input
                            type={showConfirm ? 'text' : 'password'}
                            name="confirmPassword"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="form_input"
                        />
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setShowConfirm(p => !p)}
                            className="shrink-0 px-3 text-body-color/40 hover:text-body-color/70 transition-colors"
                            aria-label={showConfirm ? 'Hide password' : 'Show password'}
                        >
                            {showConfirm ? <EyeOpenIcon /> : <EyeClosedIcon />}
                        </button>
                    </div>
                    {errors.confirmPassword && touched.confirmPassword && (
                        <p className="error_text">{errors.confirmPassword}</p>
                    )}
                </div>

                {/* Submit */}
                <Button
                    type="submit"
                    className="global_btn rounded_full w-full text-sm font-medium tracking-wide mt-2"
                    isDisabled={isSubmitting}
                >
                    Dive In
                </Button>
            </form>

            {/* Back to sign in */}
            <p className="text-sm text-center w-full text-body-color">
                Remember it?{' '}
                <span onClick={() => navigate('SignIn')} className="accent_text">
                    Sign in
                </span>
            </p>
        </div>
    )
}
